import React, { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';

export default function EditVisaModal({ application, onClose, onSuccess, url }) {
  const [formData, setFormData] = useState({
    primaryFullName: application.primaryApplicant?.fullName || '',
    primaryPassportNumber: application.primaryApplicant?.passportNumber || '',
    familyMembers: application.familyMembers ? JSON.parse(JSON.stringify(application.familyMembers)) : []
  });
  
  const [files, setFiles] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFileChange = (e, fieldName) => {
    if (e.target.files[0]) {
      setFiles({ ...files, [fieldName]: e.target.files[0] });
    }
  };

  const handleFamilyMemberChange = (index, field, value) => {
    const updated = [...formData.familyMembers];
    updated[index][field] = value;
    setFormData({ ...formData, familyMembers: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const submitData = new FormData();
      submitData.append('primaryFullName', formData.primaryFullName);
      submitData.append('primaryPassportNumber', formData.primaryPassportNumber);
      
      // Send family members as JSON string
      submitData.append('familyMembers', JSON.stringify(formData.familyMembers));

      // Append files
      Object.keys(files).forEach(key => {
        submitData.append(key, files[key]);
      });

      const endpoint = `${url}/api/visa/admin/applications/${application._id}`;

      const res = await axios.put(endpoint, submitData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      if (res.data.success) {
        toast.success('Visa application updated successfully');
        onSuccess(res.data.application);
      } else {
        toast.error(res.data.message || 'Failed to update application');
      }
    } catch (err) {
      console.error(err);
      toast.error('An error occurred while updating.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDocUrl = (filename) => {
    if (!filename) return "#";
    return `${url}/uploads/visaApplications/${filename}`;
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] flex flex-col my-8">
        <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10 rounded-t-xl">
          <h2 className="text-xl font-bold">Edit Visa Application Details</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full"><X size={20} /></button>
        </div>
        
        <div className="p-6 overflow-y-auto">
          <form id="admin-edit-visa-form" onSubmit={handleSubmit} className="space-y-6">
            
            <div className="bg-gray-50 p-4 rounded-lg border">
              <h3 className="font-bold mb-4 text-blue-600">Primary Applicant</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">Full Name</label>
                  <input type="text" value={formData.primaryFullName} onChange={e => setFormData({...formData, primaryFullName: e.target.value})} className="w-full border rounded p-2 text-sm focus:outline-blue-600" required />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">Passport Number</label>
                  <input type="text" value={formData.primaryPassportNumber} onChange={e => setFormData({...formData, primaryPassportNumber: e.target.value})} className="w-full border rounded p-2 text-sm focus:outline-blue-600" required />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-xs font-bold text-gray-500 mb-2">Update Documents (Leave blank to keep existing)</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {['passport', 'photograph', 'proofOfFunds', 'ticket'].map(doc => {
                    const currentDoc = Array.isArray(application.documents) ? application.documents.find(d => d.docName === doc) : { fileUrl: application.documents?.[doc] };
                    return (
                    <div key={doc} className="border p-2 rounded bg-white">
                      <div className="text-xs font-bold capitalize mb-1">{doc.replace(/([A-Z])/g, ' $1')}</div>
                      {currentDoc?.fileUrl && (
                        <a href={getDocUrl(currentDoc.fileUrl)} target="_blank" rel="noreferrer" className="text-xs text-blue-500 hover:underline block mb-2">View Current</a>
                      )}
                      <input type="file" onChange={e => handleFileChange(e, doc)} className="w-full text-xs" />
                    </div>
                  )})}
                </div>
              </div>
            </div>

            {formData.familyMembers.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-bold text-blue-600 border-b pb-2">Family Members</h3>
                {formData.familyMembers.map((member, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg border">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">Full Name</label>
                        <input type="text" value={member.fullName} onChange={e => handleFamilyMemberChange(index, 'fullName', e.target.value)} className="w-full border rounded p-2 text-sm focus:outline-blue-600" required />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">Passport Number</label>
                        <input type="text" value={member.passportNumber} onChange={e => handleFamilyMemberChange(index, 'passportNumber', e.target.value)} className="w-full border rounded p-2 text-sm focus:outline-blue-600" required />
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <label className="block text-xs font-bold text-gray-500 mb-2">Update Documents</label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {['passport', 'photograph', 'ticket'].map(doc => {
                          const currentDoc = Array.isArray(member.documents) ? member.documents.find(d => d.docName === doc) : { fileUrl: member.documents?.[doc] };
                          return (
                          <div key={doc} className="border p-2 rounded bg-white">
                            <div className="text-xs font-bold capitalize mb-1">{doc.replace(/([A-Z])/g, ' $1')}</div>
                            {currentDoc?.fileUrl && (
                              <a href={getDocUrl(currentDoc.fileUrl)} target="_blank" rel="noreferrer" className="text-xs text-blue-500 hover:underline block mb-2">View Current</a>
                            )}
                            <input type="file" onChange={e => handleFileChange(e, `familyMember_${index}_${doc}`)} className="w-full text-xs" />
                          </div>
                        )})}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
          </form>
        </div>
        
        <div className="p-4 border-t bg-gray-50 rounded-b-xl flex justify-end gap-3 sticky bottom-0 z-10">
          <button type="button" onClick={onClose} className="px-4 py-2 border rounded font-bold hover:bg-gray-100">Cancel</button>
          <button type="submit" form="admin-edit-visa-form" disabled={isSubmitting} className="px-4 py-2 bg-blue-600 text-white rounded font-bold hover:bg-blue-700 disabled:opacity-50">
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
